import { Check, CheckCircle2, Link2, MousePointer2, Pause, Play, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Phone } from "../components/ProductScreen.js";
import type { ScreenName } from "../components/ProductScreen.js";

interface WalkthroughStep {
  readonly number: string;
  readonly title: string;
  readonly text: string;
  readonly screen: ScreenName;
  readonly short: string;
  readonly detail: string;
}

const STEPS: readonly WalkthroughStep[] = [
  {
    number: "01",
    title: "Meet people in the moment.",
    text: "A scan or a shared link brings your community straight to your giving experience.",
    screen: "giving",
    short: "Scan or open",
    detail: "One event. One simple way in.",
  },
  {
    number: "02",
    title: "Make generosity feel effortless.",
    text: "Choose an amount, pick a payment method, and confirm. Giving comes before creating an account.",
    screen: "giving",
    short: "Choose & give",
    detail: "Choose an amount · confirm payment",
  },
  {
    number: "03",
    title: "Every gift becomes part of the story.",
    text: "Donors get a receipt. Your team gets a clear view of the gift and the progress of your event.",
    screen: "receipt",
    short: "See the impact",
    detail: "Gift complete. A moment made meaningful.",
  },
];

const STEP_DURATION_MS = 4500;

function noteIcon(step: number) {
  if (step === 2) return <CheckCircle2 size={18} />;
  if (step === 1) return <MousePointer2 size={18} />;
  return <Link2 size={18} />;
}

export function Walkthrough() {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(motion.matches);
    const listener = () => {
      setReducedMotion(motion.matches);
      if (motion.matches) setPlaying(false);
    };
    motion.addEventListener("change", listener);
    return () => motion.removeEventListener("change", listener);
  }, []);

  useEffect(() => {
    if (!playing || reducedMotion) return;
    const timer = setTimeout(() => {
      if (step < STEPS.length - 1) setStep(step + 1);
      else setPlaying(false);
    }, STEP_DURATION_MS);
    return () => clearTimeout(timer);
  }, [step, playing, reducedMotion]);

  const isLastStep = step === STEPS.length - 1;

  function togglePlayback() {
    if (isLastStep) setStep(0);
    setPlaying((value) => !value);
  }

  function selectStep(index: number) {
    setStep(index);
    setPlaying(false);
  }

  return (
    <section id="how-it-works" className="section">
      <div className="page">
        <div className="section__heading">
          <div>
            <p className="overline">
              <span className="overline__rule" aria-hidden="true" />A simple path to giving
            </p>
            <h2>
              Good intentions.
              <br />
              <em>Beautifully simple action.</em>
            </h2>
          </div>
          <p>When people feel inspired to give, make the next step the easy one.</p>
        </div>

        <div className="walkthrough__body">
          <div className="walkthrough__visual">
            <div className="walkthrough__visual-head">
              <span>The donor experience</span>
              <span className="preview-tag">Guided preview</span>
            </div>
            <div className="walkthrough__stage">
              <Phone screen={STEPS[step]!.screen} />
              <div className="walkthrough__note" key={step}>
                <span className="walkthrough__note-icon">{noteIcon(step)}</span>
                <p>{STEPS[step]!.detail}</p>
              </div>
            </div>
            <div className="walkthrough__controls">
              <span className="walkthrough__counter">
                0{step + 1} <span>/ 0{STEPS.length}</span>
              </span>
              <div className="walkthrough__progress">
                {STEPS.map((s, index) => (
                  <span key={s.number} className={index <= step ? "is-filled" : ""} />
                ))}
              </div>
              <button
                className="walkthrough__play"
                aria-label={
                  playing
                    ? "Pause walkthrough"
                    : isLastStep
                      ? "Replay walkthrough"
                      : "Play walkthrough"
                }
                onClick={togglePlayback}
                disabled={reducedMotion}
              >
                {playing ? (
                  <Pause size={16} />
                ) : isLastStep ? (
                  <RotateCcw size={16} />
                ) : (
                  <Play size={16} />
                )}
              </button>
            </div>
          </div>

          <div className="walkthrough__steps" role="group" aria-label="Giving walkthrough steps">
            {STEPS.map((s, index) => (
              <button
                key={s.number}
                className={index === step ? "walkthrough__step is-active" : "walkthrough__step"}
                aria-pressed={index === step}
                onClick={() => selectStep(index)}
              >
                <span className="walkthrough__step-number">{s.number}</span>
                <span className="walkthrough__step-copy">
                  <b>{s.title}</b>
                  <span>{s.text}</span>
                  <span className="walkthrough__step-link">
                    <Check size={12} style={{ display: "inline", marginRight: 4 }} />
                    {s.short}
                  </span>
                </span>
              </button>
            ))}
            <p className="walkthrough__explain">
              Explore the giving journey. This preview doesn't process a payment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
